import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  ArrowUpRight,
  BookOpenCheck,
  Box,
  ChevronRight,
  CircleGauge,
  FlaskConical,
  Plus,
  RotateCcw,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { useState, type ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";

import {
  WEEKS,
  clashesCaught,
  history,
  initialAssumptions,
  initialChecks,
  initialPreferences,
  initialSops,
  patterns,
  people,
  periods,
  projects,
  totals,
  workType,
  workTypes,
  type Assumptions,
  type Autonomy,
  type Check,
  type Pattern,
  type Period,
  type Preferences,
  type Severity,
  type Sop,
  type WeekRow,
  type WorkType,
} from "./-manager-data";

import "./-agent-first.css";
import "./-manager.css";

type View = "returns" | "sops" | "checks" | "preferences";
const views: View[] = ["returns", "sops", "checks", "preferences"];

export const Route = createFileRoute("/prototypes/manager")({
  validateSearch: (search: Record<string, unknown>) => ({
    view: views.find((view) => view === search.view) ?? "returns",
    sop: typeof search.sop === "string" ? search.sop : undefined,
  }),
  component: ManagerConsole,
});

const WEEKS_PER_MONTH = 4.33;
// Acceptance a work type needs over the quarter before agents may apply on their own.
const APPLY_THRESHOLD = 0.8;
const history0 = history(initialSops());
const checks0 = initialChecks();

const hours = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const money = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  maximumFractionDigits: 1,
});
const percent = (value: number) => `${Math.round(value * 100)}%`;

function weekLabel(week: number) {
  const date = new Date(2026, 4, 4 + week * 7);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function inPeriod(rows: WeekRow[], period: Period, offset = 0) {
  const end = WEEKS - offset;
  return rows.filter((row) => row.week >= end - period && row.week < end);
}

function recentTasksPerWeek(type: WorkType) {
  const rows = inPeriod(history0, 4).filter((row) => row.type === type);
  return rows.reduce((sum, row) => sum + row.tasks, 0) / 4;
}

function quarterAcceptance(type: WorkType) {
  const t = totals(inPeriod(history0, 13).filter((row) => row.type === type));
  return t.accepted / t.tasks;
}

// Monthly hours a not-yet-live SOP should return, from its expected lift.
function sopProjection(sop: Sop) {
  const spec = workType(sop.workType);
  const share = sop.projects.length / projects.length;
  return (
    recentTasksPerWeek(sop.workType) *
    share *
    (sop.acceptanceLift * spec.manualHours * 0.5 +
      sop.reviewMinutesSaved / 60) *
    WEEKS_PER_MONTH
  );
}

function patternProjection(pattern: Pattern) {
  return (pattern.hoursLost / 13) * WEEKS_PER_MONTH * 0.7;
}

function checkSignal(check: Check) {
  return check.fired ? 1 - check.dismissed / check.fired : 1;
}

// Stricter settings fire more; dismissals swing faster than real catches.
function tunedCheck(check: Check): Check {
  const base = checks0.find((initial) => initial.id === check.id);
  if (!check.param || !base?.param) return check;
  const ratio =
    check.param.stricter === "higher"
      ? check.param.value / base.param.value
      : base.param.value / check.param.value;
  const real = base.fired - base.dismissed;
  const dismissed = Math.round(base.dismissed * Math.pow(ratio, 2.5));
  const caught = Math.round(real * Math.pow(ratio, 0.6));
  return { ...check, fired: caught + dismissed, dismissed };
}

function ManagerConsole() {
  const { view, sop: sopId } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [period, setPeriod] = useState<Period>(13);
  const [assumptions, setAssumptions] = useState(initialAssumptions);
  const [sops, setSops] = useState(initialSops);
  const [checks, setChecks] = useState(initialChecks);
  const [preferences, setPreferences] = useState(initialPreferences);
  const [flash, setFlash] = useState<string | null>(null);

  function go(next: View, sop?: string) {
    void navigate({ search: { view: next, sop } });
  }
  function reset() {
    setPeriod(13);
    setAssumptions(initialAssumptions);
    setSops(initialSops());
    setChecks(initialChecks());
    setPreferences(initialPreferences());
    setFlash(null);
    go("returns");
  }
  function addressPattern(pattern: Pattern) {
    if (pattern.fix.kind === "sop") {
      const sop: Sop = {
        id: `sop-${pattern.id}`,
        title: pattern.fix.title,
        workType: pattern.workType,
        status: "draft",
        owner: "Dana Okafor",
        updated: "Today",
        acceptanceLift: Math.min(0.15, pattern.count / 100),
        reviewMinutesSaved: 3,
        projects: projects.map((project) => project.id),
        body: pattern.fix.body,
        origin: pattern.id,
      };
      setSops((current) => [...current, sop]);
      setFlash(
        "Drafted from reviewer corrections. Check the wording, then make it active.",
      );
      go("sops", sop.id);
    } else {
      const check: Check = {
        id: `check-${pattern.id}`,
        title: pattern.fix.title,
        description: pattern.fix.description,
        workType: pattern.workType,
        severity: pattern.fix.severity,
        enabled: true,
        param: pattern.fix.param,
        fired: 0,
        dismissed: 0,
        origin: pattern.id,
      };
      setChecks((current) => [check, ...current]);
      setFlash(
        "Check added and running on new agent work. It will show here as it fires.",
      );
      go("checks");
    }
  }

  const addressedBy = (pattern: Pattern) =>
    sops.find((sop) => sop.origin === pattern.id) ??
    checks.find((check) => check.origin === pattern.id);

  const crumb = {
    returns: "Returns",
    sops: "SOPs",
    checks: "Checks",
    preferences: "Preferences",
  }[view];
  const nav: { id: View; label: string; icon: ReactNode; count?: number }[] = [
    { id: "returns", label: "Returns", icon: <TrendingUp /> },
    {
      id: "sops",
      label: "SOPs",
      icon: <BookOpenCheck />,
      count: sops.filter((sop) => sop.status === "active").length,
    },
    {
      id: "checks",
      label: "Checks",
      icon: <ShieldCheck />,
      count: checks.filter((check) => check.enabled).length,
    },
    { id: "preferences", label: "Preferences", icon: <SlidersHorizontal /> },
  ];

  return (
    <div className="af-theme af-app">
      <aside className="af-sidebar">
        <Link to="/prototypes" className="af-brand">
          <span className="af-brand-icon" aria-hidden="true">
            <Box />
          </span>
          Bear<span className="af-prototype-label">PROTOTYPE</span>
        </Link>
        <div className="af-project">
          <span className="af-project-monogram">R</span>
          <div>
            <strong>Ridgeline Builders</strong>
            <span>Firm settings</span>
          </div>
        </div>
        <nav className="af-nav mg-nav" aria-label="Firm navigation">
          {nav.map((item) => (
            <button
              key={item.id}
              className={cn("af-nav-link", view === item.id && "af-nav-active")}
              onClick={() => go(item.id)}
            >
              {item.icon}
              {item.label}
              {item.count !== undefined && (
                <span className="af-nav-count">{item.count}</span>
              )}
            </button>
          ))}
        </nav>
        <div className="af-sidebar-label">
          PROJECTS<span>{projects.length}</span>
        </div>
        <nav className="af-task-nav" aria-label="Projects">
          {projects.map((project) =>
            project.id === "northline" ? (
              <Link
                key={project.id}
                to="/prototypes/agent-first"
                search={{ task: undefined }}
                className="af-task-link"
              >
                <span className="af-task-dot" data-status="review" />
                <span>{project.name}</span>
              </Link>
            ) : (
              <span key={project.id} className="af-task-link">
                <span className="af-task-dot" data-status="working" />
                <span>{project.name}</span>
              </span>
            ),
          )}
        </nav>
        <div className="af-sidebar-bottom">
          <span className="af-avatar">DO</span>
          <div>
            <strong>Dana Okafor</strong>
            <span>VP operations</span>
          </div>
          <Badge variant="outline">Demo</Badge>
        </div>
      </aside>
      <main className="af-main">
        <header className="af-topbar">
          <nav className="af-breadcrumb" aria-label="Breadcrumb">
            <ol>
              <li>Ridgeline Builders</li>
              <li aria-hidden="true">
                <ChevronRight />
              </li>
              <li aria-current="page" className="af-breadcrumb-current">
                {crumb}
              </li>
            </ol>
          </nav>
          <span className="mg-topbar-note">
            4 projects · 23 people using Bear
          </span>
        </header>
        <div className="af-overview mg-page">
          {view === "returns" && (
            <Returns
              period={period}
              setPeriod={setPeriod}
              assumptions={assumptions}
              setAssumptions={setAssumptions}
              sops={sops}
              preferences={preferences}
              addressedBy={addressedBy}
              addressPattern={addressPattern}
              go={go}
            />
          )}
          {view === "sops" && (
            <Sops
              sops={sops}
              setSops={setSops}
              selectedId={sopId ?? sops[0]?.id}
              select={(id) => go("sops", id)}
              flash={flash}
              clearFlash={() => setFlash(null)}
            />
          )}
          {view === "checks" && (
            <Checks
              checks={checks}
              setChecks={setChecks}
              flash={flash}
              clearFlash={() => setFlash(null)}
            />
          )}
          {view === "preferences" && (
            <PreferencesView
              preferences={preferences}
              setPreferences={setPreferences}
            />
          )}
        </div>
        <footer className="af-demo-footer">
          <span>
            Interactive prototype · Illustrative firm data · Resets on refresh
          </span>
          <Button variant="ghost" size="sm" onClick={reset}>
            <RotateCcw data-icon="inline-start" />
            Reset demo
          </Button>
        </footer>
      </main>
    </div>
  );
}

function PageHeading({
  eyebrow,
  title,
  children,
  actions,
}: {
  eyebrow: string;
  title: string;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mg-heading">
      <div>
        <div className="af-eyebrow">{eyebrow}</div>
        <h1>{title}</h1>
        <p>{children}</p>
      </div>
      {actions}
    </div>
  );
}

function Flash({
  message,
  clear,
}: {
  message: string | null;
  clear: () => void;
}) {
  if (!message) return null;
  return (
    <div className="mg-flash" role="status">
      <Sparkles />
      <span>{message}</span>
      <Button variant="ghost" size="sm" onClick={clear}>
        Dismiss
      </Button>
    </div>
  );
}

// ─── Returns ───────────────────────────────────────────────────────────────

function Returns({
  period,
  setPeriod,
  assumptions,
  setAssumptions,
  sops,
  preferences,
  addressedBy,
  addressPattern,
  go,
}: {
  period: Period;
  setPeriod: (period: Period) => void;
  assumptions: Assumptions;
  setAssumptions: (assumptions: Assumptions) => void;
  sops: Sop[];
  preferences: Preferences;
  addressedBy: (pattern: Pattern) => Sop | Check | undefined;
  addressPattern: (pattern: Pattern) => void;
  go: (view: View, sop?: string) => void;
}) {
  const rows = inPeriod(history0, period);
  const previous = period < WEEKS ? inPeriod(history0, period, period) : null;
  const current = totals(rows);
  const prior = previous && totals(previous);
  const months = period / WEEKS_PER_MONTH;
  const clashes = clashesCaught(rows);
  const reworkAvoided =
    clashes * assumptions.fieldShare * assumptions.reworkCost;
  const value = current.hours * assumptions.rate + reworkAvoided;
  const cost = assumptions.monthlyCost * months;
  const pending = sops
    .filter((sop) => sop.status === "active" && sop.adoptedWeek === undefined)
    .reduce((sum, sop) => sum + sopProjection(sop), 0);
  const checkGains = patterns
    .filter((pattern) => {
      const fix = addressedBy(pattern);
      return fix && "severity" in fix && fix.enabled;
    })
    .reduce((sum, pattern) => sum + patternProjection(pattern), 0);
  const autonomyGains = workTypes
    .filter((spec) => preferences.autonomy[spec.id] === "apply")
    .reduce((sum, spec) => sum + autonomySavings(spec.id), 0);
  const projected = pending + checkGains + autonomyGains;

  return (
    <>
      <PageHeading
        eyebrow="RETURNS · ALL PROJECTS"
        title="Is Bear paying for itself?"
        actions={
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            className="mg-period"
            value={String(period)}
            onValueChange={(value) =>
              value && setPeriod(Number(value) as Period)
            }
          >
            {periods.map((option) => (
              <ToggleGroupItem key={option.value} value={String(option.value)}>
                {option.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        }
      >
        Hours your team got back from agent work, what that is worth, and where
        reviewers are still correcting it.
      </PageHeading>

      <section className="mg-kpis" aria-label="Headline numbers">
        <Stat
          label="Hours returned"
          value={hours.format(current.hours)}
          delta={prior && delta(current.hours, prior.hours)}
          note={`${hours.format(current.reviewHours)} h spent reviewing`}
        />
        <Stat
          label="Value returned"
          value={money.format(value)}
          note={`Includes ${money.format(reworkAvoided)} field rework avoided`}
        />
        <Stat
          label="Return on spend"
          value={`${(value / cost).toFixed(1)}×`}
          note={`Against ${money.format(cost)} of Bear`}
          hero
        />
        <Stat
          label="Accepted as-is"
          value={percent(current.accepted / current.tasks)}
          delta={
            prior &&
            pointDelta(
              current.accepted / current.tasks,
              prior.accepted / prior.tasks,
            )
          }
          note={`${hours.format(current.tasks)} agent tasks reviewed`}
        />
        <Stat
          label="Clashes cleared before the field"
          value={hours.format(clashes)}
          note={`About ${hours.format(clashes * assumptions.fieldShare)} would have reached the field`}
        />
      </section>

      <Assumptions assumptions={assumptions} setAssumptions={setAssumptions} />

      <section className="mg-panel" aria-labelledby="weekly-heading">
        <div className="mg-panel-heading">
          <div>
            <h2 id="weekly-heading">Hours returned each week</h2>
            <p>Markers show when a firm SOP went live.</p>
          </div>
        </div>
        <WeeklyChart rows={rows} sops={sops} />
      </section>

      {projected > 0 && (
        <section className="mg-projection" aria-label="Projected effect">
          <TrendingUp />
          <div>
            <strong>
              About {hours.format(projected)} more hours a month from changes
              you made today
            </strong>
            <span>
              Projected from the corrections each change addresses. It will be
              measured here once the agents have run under it for two weeks.
            </span>
          </div>
        </section>
      )}

      <section aria-labelledby="corrections-heading">
        <div className="af-section-heading mg-section-heading">
          <h2 id="corrections-heading">
            <CircleGauge />
            Where reviewers keep correcting agents
            <span>Last 13 weeks</span>
          </h2>
        </div>
        <div className="mg-patterns">
          {patterns.map((pattern) => {
            const fix = addressedBy(pattern);
            return (
              <article key={pattern.id} className="mg-pattern">
                <div className="mg-pattern-meta">
                  <span>{workType(pattern.workType).name}</span>
                  <span>
                    {pattern.count} corrections · {pattern.hoursLost} h lost
                  </span>
                </div>
                <h3>{pattern.title}</h3>
                <p>{pattern.detail}</p>
                <Collapsible>
                  <CollapsibleTrigger className="mg-examples-trigger">
                    <ChevronRight />
                    Examples
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <ul className="mg-examples">
                      {pattern.examples.map((example) => (
                        <li key={example}>{example}</li>
                      ))}
                    </ul>
                  </CollapsibleContent>
                </Collapsible>
                <div className="mg-pattern-action">
                  {fix ? (
                    <button
                      className="mg-addressed"
                      onClick={() =>
                        "severity" in fix ? go("checks") : go("sops", fix.id)
                      }
                    >
                      <ShieldCheck />
                      <span>
                        {"severity" in fix
                          ? `Check: ${fix.title}`
                          : `SOP: ${fix.title}`}
                      </span>
                      <ArrowUpRight />
                    </button>
                  ) : (
                    <>
                      <span>
                        Recovers about{" "}
                        {hours.format(patternProjection(pattern))} h a month
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => addressPattern(pattern)}
                      >
                        {pattern.fix.kind === "sop"
                          ? "Draft an SOP"
                          : "Add a check"}
                        <ArrowRight data-icon="inline-end" />
                      </Button>
                    </>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="mg-panel" aria-labelledby="types-heading">
        <div className="mg-panel-heading">
          <div>
            <h2 id="types-heading">By kind of work</h2>
            <p>
              Accepted as-is over the period. Agents earn more autonomy as this
              rises.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => go("preferences")}>
            Autonomy settings
            <ArrowUpRight data-icon="inline-end" />
          </Button>
        </div>
        <table className="mg-table">
          <thead>
            <tr>
              <th>Work</th>
              <th className="mg-num">Tasks</th>
              <th>Accepted as-is</th>
              <th className="mg-num">Review</th>
              <th className="mg-num">Hours returned</th>
              <th className="mg-num">Value</th>
              <th>Agents may</th>
            </tr>
          </thead>
          <tbody>
            {workTypes.map((spec) => {
              const typeRows = rows.filter((row) => row.type === spec.id);
              const t = totals(typeRows);
              const last = typeRows[typeRows.length - 1];
              return (
                <tr key={spec.id}>
                  <td>{spec.name}</td>
                  <td className="mg-num">{t.tasks}</td>
                  <td>
                    <span className="mg-acceptance">
                      <Sparkline
                        values={typeRows.map((row) => row.accepted / row.tasks)}
                      />
                      {percent(t.accepted / t.tasks)}
                    </span>
                  </td>
                  <td className="mg-num">{last.reviewMinutes} min</td>
                  <td className="mg-num">{hours.format(t.hours)} h</td>
                  <td className="mg-num">
                    {money.format(t.hours * assumptions.rate)}
                  </td>
                  <td>
                    <span
                      className="mg-autonomy"
                      data-level={preferences.autonomy[spec.id]}
                    >
                      {autonomyLabels[preferences.autonomy[spec.id]]}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <section className="mg-panel" aria-labelledby="projects-heading">
        <div className="mg-panel-heading">
          <div>
            <h2 id="projects-heading">By project</h2>
            <p>
              Cedar Point trails the firm. The takeoff SOP does not cover it
              yet.
            </p>
          </div>
        </div>
        <table className="mg-table">
          <thead>
            <tr>
              <th>Project</th>
              <th>Phase</th>
              <th className="mg-num">Tasks</th>
              <th className="mg-num">Accepted as-is</th>
              <th className="mg-num">Hours returned</th>
              <th className="mg-num">SOPs in force</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((project) => {
              const acceptance =
                current.accepted / current.tasks + project.acceptanceDelta;
              const coverage = sops.filter(
                (sop) =>
                  sop.status === "active" && sop.projects.includes(project.id),
              ).length;
              return (
                <tr key={project.id}>
                  <td>{project.name}</td>
                  <td className="mg-muted">{project.phase}</td>
                  <td className="mg-num">
                    {Math.round(current.tasks * project.share)}
                  </td>
                  <td className="mg-num">{percent(acceptance)}</td>
                  <td className="mg-num">
                    {hours.format(
                      current.hours *
                        project.share *
                        (1 + project.acceptanceDelta * 2),
                    )}{" "}
                    h
                  </td>
                  <td className="mg-num">{coverage}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </>
  );
}

function delta(now: number, before: number) {
  const change = (now - before) / before;
  return {
    text: `${change >= 0 ? "+" : "−"}${percent(Math.abs(change))} vs prior`,
    up: change >= 0,
  };
}

function pointDelta(now: number, before: number) {
  const points = Math.round((now - before) * 100);
  return {
    text: `${points >= 0 ? "+" : "−"}${Math.abs(points)} pts vs prior`,
    up: points >= 0,
  };
}

function Stat({
  label,
  value,
  delta,
  note,
  hero,
}: {
  label: string;
  value: string;
  delta?: { text: string; up: boolean } | null;
  note: string;
  hero?: boolean;
}) {
  return (
    <div className={cn("mg-stat", hero && "mg-stat-hero")}>
      <span className="mg-stat-label">{label}</span>
      <strong>{value}</strong>
      {delta && (
        <span className="mg-delta" data-up={delta.up}>
          {delta.text}
        </span>
      )}
      <small>{note}</small>
    </div>
  );
}

function Assumptions({
  assumptions,
  setAssumptions,
}: {
  assumptions: Assumptions;
  setAssumptions: (assumptions: Assumptions) => void;
}) {
  const fields: {
    key: keyof Assumptions;
    label: string;
    prefix?: string;
    suffix?: string;
    scale?: number;
  }[] = [
    { key: "rate", label: "Blended hourly rate", prefix: "$", suffix: "/ h" },
    { key: "monthlyCost", label: "Bear cost", prefix: "$", suffix: "/ mo" },
    { key: "reworkCost", label: "Field rework per clash", prefix: "$" },
    {
      key: "fieldShare",
      label: "Clashes that would reach the field",
      suffix: "%",
      scale: 100,
    },
  ];
  return (
    <Collapsible className="mg-assumptions">
      <CollapsibleTrigger className="mg-assumptions-trigger">
        <ChevronRight />
        <span>How we count</span>
        <small>
          ${assumptions.rate}/h · {money.format(assumptions.monthlyCost)}/mo ·{" "}
          {percent(assumptions.fieldShare)} of clashes reach the field
        </small>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <p className="mg-method">
          A task accepted as-is returns the hours it takes your team by hand,
          less review. An edited task returns half. A rejected task costs its
          review time. Value adds the field rework avoided on clashes agents
          cleared. Change these to match your books.
        </p>
        <div className="mg-assumption-fields">
          {fields.map((field) => {
            const scale = field.scale ?? 1;
            return (
              <label key={field.key}>
                <span>{field.label}</span>
                <span className="mg-input">
                  {field.prefix && <i>{field.prefix}</i>}
                  <Input
                    type="number"
                    min={0}
                    value={Math.round(assumptions[field.key] * scale)}
                    onChange={(event) =>
                      setAssumptions({
                        ...assumptions,
                        [field.key]: Number(event.target.value) / scale,
                      })
                    }
                  />
                  {field.suffix && <i>{field.suffix}</i>}
                </span>
              </label>
            );
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function WeeklyChart({ rows, sops }: { rows: WeekRow[]; sops: Sop[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const weeks = [...new Set(rows.map((row) => row.week))].map((week) => ({
    week,
    ...totals(rows.filter((row) => row.week === week)),
  }));
  const width = 760;
  const height = 220;
  const pad = { top: 34, right: 8, bottom: 26, left: 40 };
  const max = Math.ceil(Math.max(...weeks.map((week) => week.hours)) / 50) * 50;
  const band = (width - pad.left - pad.right) / weeks.length;
  const barWidth = Math.min(24, band - 2);
  const y = (value: number) =>
    pad.top + (height - pad.top - pad.bottom) * (1 - value / max);
  const ticks = [0, max / 2, max];
  const live = sops.filter(
    (sop) =>
      sop.adoptedWeek !== undefined &&
      weeks.some((week) => week.week === sop.adoptedWeek),
  );
  const hovered = weeks.find((week) => week.week === hover);
  const labelEvery = weeks.length > 13 ? 4 : weeks.length > 4 ? 2 : 1;
  return (
    <div className="mg-chart">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="Hours returned each week"
        onMouseLeave={() => setHover(null)}
      >
        {ticks.map((tick) => (
          <g key={tick} className="mg-grid">
            <line
              x1={pad.left}
              x2={width - pad.right}
              y1={y(tick)}
              y2={y(tick)}
            />
            <text x={pad.left - 8} y={y(tick) + 3} textAnchor="end">
              {tick}
            </text>
          </g>
        ))}
        {live.map((sop, order) => {
          const index = weeks.findIndex(
            (week) => week.week === sop.adoptedWeek,
          );
          const x = pad.left + band * index + 1;
          return (
            <g key={sop.id} className="mg-marker">
              <line
                x1={x}
                x2={x}
                y1={8 + (order % 2) * 12}
                y2={height - pad.bottom}
              />
              <text x={x + 5} y={14 + (order % 2) * 12}>
                {sop.title}
              </text>
            </g>
          );
        })}
        {weeks.map((week, index) => {
          const cx = pad.left + band * index + band / 2;
          const top = y(week.hours);
          const bottom = y(0);
          const r = Math.min(4, bottom - top);
          return (
            <g key={week.week} onMouseEnter={() => setHover(week.week)}>
              <rect
                className="mg-hit"
                x={pad.left + band * index}
                y={pad.top}
                width={band}
                height={height - pad.top - pad.bottom}
              />
              <path
                className={cn("mg-bar", hover === week.week && "mg-bar-hover")}
                d={`M${cx - barWidth / 2},${bottom} V${top + r} q0,-${r} ${r},-${r} H${cx + barWidth / 2 - r} q${r},0 ${r},${r} V${bottom} Z`}
              />
              {index % labelEvery === 0 && (
                <text
                  className="mg-axis"
                  x={cx}
                  y={height - 8}
                  textAnchor="middle"
                >
                  {weekLabel(week.week)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      {hovered && (
        <div
          className="mg-tooltip"
          style={{
            left: `${((pad.left + band * weeks.indexOf(hovered) + band / 2) / width) * 100}%`,
          }}
        >
          <strong>Week of {weekLabel(hovered.week)}</strong>
          <span>{hours.format(hovered.hours)} h returned</span>
          <span>
            {hovered.tasks} tasks · {percent(hovered.accepted / hovered.tasks)}{" "}
            accepted as-is
          </span>
        </div>
      )}
    </div>
  );
}

function Sparkline({ values }: { values: number[] }) {
  const width = 72;
  const height = 22;
  const min = 0.4;
  const max = 1;
  const points = values.map((value, index) => [
    (index / Math.max(1, values.length - 1)) * (width - 6) + 3,
    height - 3 - ((value - min) / (max - min)) * (height - 6),
  ]);
  const last = points[points.length - 1];
  return (
    <svg
      className="mg-sparkline"
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden="true"
    >
      <polyline points={points.map((point) => point.join(",")).join(" ")} />
      <circle cx={last[0]} cy={last[1]} r={3} />
    </svg>
  );
}

// ─── SOPs ──────────────────────────────────────────────────────────────────

function Sops({
  sops,
  setSops,
  selectedId,
  select,
  flash,
  clearFlash,
}: {
  sops: Sop[];
  setSops: (update: (sops: Sop[]) => Sop[]) => void;
  selectedId?: string;
  select: (id: string) => void;
  flash: string | null;
  clearFlash: () => void;
}) {
  const selected = sops.find((sop) => sop.id === selectedId);
  function update(sop: Sop) {
    setSops((current) =>
      current.map((existing) =>
        existing.id === sop.id ? { ...sop, updated: "Today" } : existing,
      ),
    );
  }
  function create() {
    const sop: Sop = {
      id: crypto.randomUUID(),
      title: "Untitled SOP",
      workType: "coordination",
      status: "draft",
      owner: "Dana Okafor",
      updated: "Today",
      acceptanceLift: 0.04,
      reviewMinutesSaved: 2,
      projects: [],
      body: "1. ",
    };
    setSops((current) => [...current, sop]);
    select(sop.id);
  }
  return (
    <>
      <PageHeading
        eyebrow="SOPS · HOW RIDGELINE WORKS"
        title="Standard operating procedures"
        actions={
          <Button onClick={create}>
            <Plus data-icon="inline-start" />
            New SOP
          </Button>
        }
      >
        Agents read the SOPs for their kind of work before every task, the same
        way a new hire would. Write them the way you would brief one.
      </PageHeading>
      <Flash message={flash} clear={clearFlash} />
      <div className="mg-split">
        <nav className="mg-list" aria-label="SOPs">
          {sops.map((sop) => (
            <button
              key={sop.id}
              className={cn(
                "mg-list-item",
                sop.id === selected?.id && "mg-list-active",
              )}
              onClick={() => select(sop.id)}
            >
              <span className="mg-sop-status" data-status={sop.status}>
                {sopStatusLabels[sop.status]}
              </span>
              <strong>{sop.title}</strong>
              <small>
                {workType(sop.workType).name} ·{" "}
                {projectCount(sop.projects.length)}
              </small>
            </button>
          ))}
        </nav>
        {selected && (
          <SopEditor key={selected.id} sop={selected} update={update} />
        )}
      </div>
    </>
  );
}

const sopStatusLabels: Record<Sop["status"], string> = {
  active: "Active",
  draft: "Draft",
  paused: "Paused",
};

function projectCount(count: number) {
  if (count === projects.length) return "All projects";
  if (count === 0) return "No projects yet";
  if (count === 1) return "1 project";
  return `${count} projects`;
}

function SopEditor({ sop, update }: { sop: Sop; update: (sop: Sop) => void }) {
  const [replayed, setReplayed] = useState(false);
  const effect = sopEffect(sop);
  const replay = replayResult(sop);
  return (
    <article className="mg-editor">
      <div className="mg-editor-heading">
        <Input
          className="mg-title-input"
          aria-label="SOP title"
          value={sop.title}
          onChange={(event) => update({ ...sop, title: event.target.value })}
        />
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={sop.status}
          onValueChange={(status) =>
            status && update({ ...sop, status: status as Sop["status"] })
          }
        >
          <ToggleGroupItem value="draft">Draft</ToggleGroupItem>
          <ToggleGroupItem value="active">Active</ToggleGroupItem>
          <ToggleGroupItem value="paused">Paused</ToggleGroupItem>
        </ToggleGroup>
      </div>
      <p className="mg-editor-meta">
        Owned by {sop.owner} · Updated {sop.updated}
        {sop.origin && " · Drafted from reviewer corrections"}
      </p>

      <div className="mg-editor-grid">
        <div className="mg-field">
          <span>Applies to</span>
          <Select
            value={sop.workType}
            onValueChange={(value) =>
              update({ ...sop, workType: value as WorkType })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="af-theme">
              {workTypes.map((spec) => (
                <SelectItem key={spec.id} value={spec.id}>
                  {spec.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <fieldset className="mg-field">
          <legend>Projects</legend>
          <div className="mg-projects">
            {projects.map((project) => (
              <label key={project.id}>
                <Checkbox
                  checked={sop.projects.includes(project.id)}
                  onCheckedChange={(checked) =>
                    update({
                      ...sop,
                      projects: checked
                        ? [...sop.projects, project.id]
                        : sop.projects.filter((id) => id !== project.id),
                    })
                  }
                />
                {project.name}
              </label>
            ))}
          </div>
        </fieldset>
      </div>

      <label className="mg-field">
        <span>Procedure</span>
        <Textarea
          className="mg-body"
          value={sop.body}
          rows={8}
          onChange={(event) => update({ ...sop, body: event.target.value })}
        />
      </label>

      <section className="mg-effect" aria-label="Effect">
        {effect ? (
          <>
            <div className="af-eyebrow">
              SINCE IT WENT LIVE ·{" "}
              {weekLabel(sop.adoptedWeek ?? 0).toUpperCase()}
            </div>
            <div className="mg-effect-row">
              <EffectFigure
                label="Accepted as-is"
                before={percent(effect.before)}
                after={percent(effect.after)}
              />
              <EffectFigure
                label="Median review"
                before={`${effect.reviewBefore} min`}
                after={`${effect.reviewAfter} min`}
              />
              <EffectFigure
                label="Hours returned a month"
                before={hours.format(effect.hoursBefore)}
                after={hours.format(effect.hoursAfter)}
              />
            </div>
          </>
        ) : (
          <>
            <div className="af-eyebrow">BEFORE IT GOES LIVE</div>
            <p>
              Replay recent {workType(sop.workType).name.toLowerCase()} tasks
              with this SOP in the agent's brief and compare the result with
              what your reviewers actually sent.
            </p>
            {replayed ? (
              <div className="mg-replay">
                <FlaskConical />
                <div>
                  <strong>
                    {replay.match} of {replay.total} replays now match what
                    reviewers sent, up from {replay.before}.
                  </strong>
                  <span>
                    Projected about{" "}
                    {hours.format(
                      sopProjection({
                        ...sop,
                        projects: sop.projects.length
                          ? sop.projects
                          : projects.map((p) => p.id),
                      }),
                    )}{" "}
                    more hours a month once it is active on its projects.
                  </span>
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setReplayed(true)}
              >
                <FlaskConical data-icon="inline-start" />
                Replay the last {replay.total} tasks
              </Button>
            )}
          </>
        )}
      </section>
    </article>
  );
}

function EffectFigure({
  label,
  before,
  after,
}: {
  label: string;
  before: string;
  after: string;
}) {
  return (
    <div className="mg-effect-figure">
      <span>{label}</span>
      <div>
        <s>{before}</s>
        <ArrowRight />
        <strong>{after}</strong>
      </div>
    </div>
  );
}

function sopEffect(sop: Sop) {
  if (sop.adoptedWeek === undefined) return null;
  const adopted = sop.adoptedWeek;
  const rows = history0.filter((row) => row.type === sop.workType);
  const before = rows.filter(
    (row) => row.week < adopted && row.week >= adopted - 6,
  );
  const after = rows.filter((row) => row.week >= adopted);
  const b = totals(before);
  const a = totals(after);
  const perMonth = (t: ReturnType<typeof totals>, weeks: number) =>
    (t.hours / weeks) * WEEKS_PER_MONTH;
  return {
    before: b.accepted / b.tasks,
    after: a.accepted / a.tasks,
    reviewBefore: before[0].reviewMinutes,
    reviewAfter: after[after.length - 1].reviewMinutes,
    hoursBefore: perMonth(b, before.length),
    hoursAfter: perMonth(a, after.length),
  };
}

function replayResult(sop: Sop) {
  const total = 12;
  const before = Math.round(total * workType(sop.workType).accepted);
  return {
    total,
    before,
    match: Math.min(
      total,
      before + Math.max(1, Math.round(total * sop.acceptanceLift)),
    ),
  };
}

// ─── Checks ────────────────────────────────────────────────────────────────

const severityLabels: Record<Severity, string> = {
  block: "Block",
  warn: "Warn",
  note: "Note",
};

function Checks({
  checks,
  setChecks,
  flash,
  clearFlash,
}: {
  checks: Check[];
  setChecks: (update: (checks: Check[]) => Check[]) => void;
  flash: string | null;
  clearFlash: () => void;
}) {
  function update(check: Check) {
    setChecks((current) =>
      current.map((existing) => (existing.id === check.id ? check : existing)),
    );
  }
  return (
    <>
      <PageHeading
        eyebrow="CHECKS · BEFORE A REVIEWER SEES IT"
        title="Firm checks"
      >
        Every agent proposal runs these before it reaches a reviewer. A blocking
        check sends the work back to the agent. A warning travels with the work.
      </PageHeading>
      <Flash message={flash} clear={clearFlash} />
      <div className="mg-checks">
        {checks.map((check) => {
          const tuned = tunedCheck(check);
          const signal = checkSignal(tuned);
          const noisy = tuned.fired >= 5 && signal < 0.7;
          return (
            <article
              key={check.id}
              className={cn("mg-check", !check.enabled && "mg-check-off")}
            >
              <button
                role="switch"
                aria-checked={check.enabled}
                aria-label={
                  check.enabled ? "Turn off this check" : "Turn on this check"
                }
                className="mg-switch"
                onClick={() => update({ ...check, enabled: !check.enabled })}
              >
                <span />
              </button>
              <div className="mg-check-copy">
                <h3>{check.title}</h3>
                <p>{check.description}</p>
                <div className="mg-check-controls">
                  <span className="mg-tag">
                    {check.workType === "all"
                      ? "All work"
                      : workType(check.workType).name}
                  </span>
                  <ToggleGroup
                    type="single"
                    variant="outline"
                    size="sm"
                    value={check.severity}
                    onValueChange={(severity) =>
                      severity &&
                      update({ ...check, severity: severity as Severity })
                    }
                  >
                    {(["block", "warn", "note"] as const).map((severity) => (
                      <ToggleGroupItem key={severity} value={severity}>
                        {severityLabels[severity]}
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>
                  {check.param && (
                    <label className="mg-param">
                      <span>{check.param.label}</span>
                      <span className="mg-input">
                        <Input
                          type="number"
                          min={1}
                          value={check.param.value}
                          onChange={(event) => {
                            const value = Number(event.target.value);
                            if (value > 0 && check.param)
                              update({
                                ...check,
                                param: { ...check.param, value },
                              });
                          }}
                        />
                        <i>{check.param.unit}</i>
                      </span>
                    </label>
                  )}
                </div>
                {noisy && (
                  <p className="mg-noisy">
                    Noisy: reviewers dismiss {percent(1 - signal)} of these.
                    {check.param
                      ? " Try a looser threshold and watch the dismissals fall."
                      : " Consider narrowing it or making it a note."}
                  </p>
                )}
              </div>
              <div className="mg-check-stats">
                {tuned.fired === 0 ? (
                  <span className="mg-muted">No runs yet</span>
                ) : (
                  <>
                    <strong>{tuned.fired}</strong>
                    <span>fired · 13 wk</span>
                    <Meter value={signal} />
                    <span>{percent(signal)} kept by reviewers</span>
                  </>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </>
  );
}

function Meter({ value }: { value: number }) {
  return (
    <span
      className="mg-meter"
      data-level={value >= 0.85 ? "good" : value >= 0.7 ? "fair" : "poor"}
      role="meter"
      aria-valuenow={Math.round(value * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <span style={{ width: `${value * 100}%` }} />
    </span>
  );
}

// ─── Preferences ───────────────────────────────────────────────────────────

const autonomyLabels: Record<Autonomy, string> = {
  suggest: "Suggest only",
  review: "Prepare for review",
  apply: "Apply and notify",
};

// Reviewer hours a month saved when agents apply accepted-as-is work themselves.
function autonomySavings(type: WorkType) {
  const spec = workType(type);
  return (
    recentTasksPerWeek(type) *
    quarterAcceptance(type) *
    (spec.reviewMinutes / 60) *
    WEEKS_PER_MONTH
  );
}

function PreferencesView({
  preferences,
  setPreferences,
}: {
  preferences: Preferences;
  setPreferences: (preferences: Preferences) => void;
}) {
  return (
    <>
      <PageHeading
        eyebrow="PREFERENCES · FIRM DEFAULTS"
        title="How much agents do on their own"
      >
        Projects inherit these. Agents can apply work themselves only for kinds
        of work they get right at least {percent(APPLY_THRESHOLD)} of the time.
      </PageHeading>
      <section className="mg-panel" aria-labelledby="autonomy-heading">
        <div className="mg-panel-heading">
          <div>
            <h2 id="autonomy-heading">Autonomy and review</h2>
            <p>Who reviews each kind of work, and how far agents go first.</p>
          </div>
        </div>
        <div className="mg-autonomy-rows">
          {workTypes.map((spec) => {
            const acceptance = quarterAcceptance(spec.id);
            const eligible = acceptance >= APPLY_THRESHOLD;
            const level = preferences.autonomy[spec.id];
            return (
              <div key={spec.id} className="mg-autonomy-row">
                <div>
                  <strong>{spec.name}</strong>
                  <span>{percent(acceptance)} accepted as-is this quarter</span>
                </div>
                <ToggleGroup
                  type="single"
                  variant="outline"
                  size="sm"
                  className="mg-level"
                  value={level}
                  onValueChange={(value) =>
                    value &&
                    setPreferences({
                      ...preferences,
                      autonomy: {
                        ...preferences.autonomy,
                        [spec.id]: value as Autonomy,
                      },
                    })
                  }
                >
                  <ToggleGroupItem value="suggest">
                    Suggest only
                  </ToggleGroupItem>
                  <ToggleGroupItem value="review">
                    Prepare for review
                  </ToggleGroupItem>
                  <ToggleGroupItem value="apply" disabled={!eligible}>
                    Apply and notify
                  </ToggleGroupItem>
                </ToggleGroup>
                <Select
                  value={preferences.reviewer[spec.id]}
                  onValueChange={(value) =>
                    setPreferences({
                      ...preferences,
                      reviewer: { ...preferences.reviewer, [spec.id]: value },
                    })
                  }
                >
                  <SelectTrigger
                    className="mg-reviewer"
                    aria-label={`Reviewer for ${spec.name}`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="af-theme">
                    {people.map((person) => (
                      <SelectItem key={person.id} value={person.id}>
                        {person.name} · {person.role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="mg-autonomy-note">
                  {level === "apply"
                    ? `Frees about ${hours.format(autonomySavings(spec.id))} reviewer hours a month`
                    : eligible
                      ? "Eligible to apply on its own"
                      : `Needs ${percent(APPLY_THRESHOLD)} to apply on its own`}
                </span>
              </div>
            );
          })}
        </div>
      </section>
      <section className="mg-panel" aria-labelledby="defaults-heading">
        <div className="mg-panel-heading">
          <div>
            <h2 id="defaults-heading">Firm defaults</h2>
            <p>What every new project starts with.</p>
          </div>
        </div>
        <div className="mg-defaults">
          <div className="mg-default">
            <div>
              <strong>Units</strong>
              <span>Quantities, dimensions and agent replies.</span>
            </div>
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              value={preferences.units}
              onValueChange={(units) =>
                units &&
                setPreferences({
                  ...preferences,
                  units: units as Preferences["units"],
                })
              }
            >
              <ToggleGroupItem value="imperial">Imperial</ToggleGroupItem>
              <ToggleGroupItem value="metric">Metric</ToggleGroupItem>
            </ToggleGroup>
          </div>
          <div className="mg-default">
            <div>
              <strong>Returns digest</strong>
              <span>A summary of this page, emailed to you.</span>
            </div>
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              value={preferences.digest}
              onValueChange={(digest) =>
                digest &&
                setPreferences({
                  ...preferences,
                  digest: digest as Preferences["digest"],
                })
              }
            >
              <ToggleGroupItem value="daily">Daily</ToggleGroupItem>
              <ToggleGroupItem value="weekly">Weekly</ToggleGroupItem>
              <ToggleGroupItem value="off">Off</ToggleGroupItem>
            </ToggleGroup>
          </div>
          <div className="mg-default">
            <div>
              <strong>Quiet hours</strong>
              <span>
                Hold trade notifications from 6 pm to 7 am and on weekends.
              </span>
            </div>
            <button
              role="switch"
              aria-checked={preferences.quietHours}
              aria-label="Quiet hours"
              className="mg-switch"
              onClick={() =>
                setPreferences({
                  ...preferences,
                  quietHours: !preferences.quietHours,
                })
              }
            >
              <span />
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
